import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Linkedin, Mail, MapPin } from 'lucide-react';

/* =====================================================================
   SKI AXIS — Global Footer (cinematic dark/gold, synced with landing)
===================================================================== */

const FOOTER_STYLES = `
.skia-footer *{box-sizing:border-box;}
.skia-footer{
  --ink:#0D1B2A;--ink2:#0F2030;--ink3:#FFFFFF;
  --gold:#C8A84B;--gold2:#D9BC6A;
  --cream:#FAF7F0;--mist:#A0B0C0;--stone:#7A8190;
  background:var(--ink);color:var(--cream);
  font-family:'DM Sans',-apple-system,BlinkMacSystemFont,sans-serif;
  border-top:1px solid rgba(200,168,75,.12);
  padding:80px 32px 32px;
  position:relative;overflow:hidden;
}
.skia-footer::before{content:'';position:absolute;top:0;left:50%;transform:translateX(-50%);width:120px;height:1px;background:linear-gradient(to right,transparent,var(--gold),transparent);}
.skia-footer .f-inner{max-width:1320px;margin:0 auto;}
.skia-footer .f-grid{display:grid;grid-template-columns:1.4fr 1fr 1fr 1fr;gap:64px;padding-bottom:56px;border-bottom:1px solid rgba(200,168,75,.1);}
@media (max-width:900px){.skia-footer .f-grid{grid-template-columns:1fr 1fr;gap:40px;}}
@media (max-width:560px){.skia-footer .f-grid{grid-template-columns:1fr;gap:32px;}}
.skia-footer .f-brand{font-family:'Cormorant Display',Georgia,serif;font-size:32px;font-weight:700;letter-spacing:.06em;color:var(--gold);line-height:1;margin-bottom:8px;}
.skia-footer .f-brand em{font-style:italic;font-weight:300;color:var(--cream);}
.skia-footer .f-tagline{font-size:11px;font-weight:600;letter-spacing:.28em;text-transform:uppercase;color:var(--stone);margin-bottom:24px;}
.skia-footer .f-desc{font-family:'Cormorant Display',Georgia,serif;font-size:18px;font-weight:300;font-style:italic;color:var(--mist);line-height:1.55;max-width:380px;margin-bottom:24px;}
.skia-footer .f-meta{display:flex;flex-direction:column;gap:8px;color:var(--mist);font-size:13px;}
.skia-footer .f-meta a{color:var(--mist);text-decoration:none;display:inline-flex;align-items:center;gap:8px;transition:color .2s;}
.skia-footer .f-meta a:hover{color:var(--gold);}
.skia-footer .f-col-title{font-size:11px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:var(--gold);margin-bottom:20px;}
.skia-footer .f-col ul{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:12px;}
.skia-footer .f-col a,.skia-footer .f-col button{background:none;border:none;padding:0;cursor:pointer;font-family:inherit;text-align:left;font-size:14px;color:var(--mist);text-decoration:none;transition:color .2s;}
.skia-footer .f-col a:hover,.skia-footer .f-col button:hover{color:var(--gold);}
.skia-footer .f-bottom{display:flex;justify-content:space-between;align-items:center;padding-top:32px;flex-wrap:wrap;gap:16px;}
.skia-footer .f-copy{font-size:11px;font-weight:500;letter-spacing:.18em;text-transform:uppercase;color:var(--stone);}
.skia-footer .f-social{display:flex;gap:12px;}
.skia-footer .f-social a{width:36px;height:36px;border-radius:50%;background:var(--ink3);border:1px solid rgba(200,168,75,.15);display:inline-flex;align-items:center;justify-content:center;color:var(--mist);transition:all .2s;}
.skia-footer .f-social a:hover{color:var(--gold);border-color:var(--gold);transform:translateY(-2px);}
`;

export const Footer = () => {
  const navigate = useNavigate();

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: FOOTER_STYLES }} />
      <footer className="skia-footer" data-testid="global-footer">
        <div className="f-inner">
          <div className="f-grid">
            <div>
              <div className="f-brand">SKI<em>AXIS</em></div>
              <div className="f-tagline">Governance · Innovation · AI</div>
              <p className="f-desc">
                A Dubai-based, world-class accelerator where 20 chosen founders per cohort scale with institutional governance, AI-powered intelligence, and direct access to global capital.
              </p>
              <div className="f-meta">
                <a href="mailto:hello@skiaxis.com"><Mail size={14} /> hello@skiaxis.com</a>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <MapPin size={14} /> Dubai University City · UAE
                </span>
              </div>
            </div>

            <div className="f-col">
              <div className="f-col-title">Programs</div>
              <ul>
                <li><button onClick={() => navigate('/apply')} data-testid="footer-cohort-1">Cohort 1 · July 2026</button></li>
                <li><button onClick={() => navigate('/masterclass')} data-testid="footer-masterclass">Masterclass</button></li>
                <li><button onClick={() => navigate('/governance')} data-testid="footer-governance">Governance</button></li>
                <li><button onClick={() => navigate('/axis-index')} data-testid="footer-axis-index">The Axis Index™</button></li>
                <li><button onClick={() => navigate('/mentors')} data-testid="footer-mentors">Mentors</button></li>
              </ul>
            </div>

            <div className="f-col">
              <div className="f-col-title">Events</div>
              <ul>
                <li><button onClick={() => navigate('/events/invest-uae-forum-2026')} data-testid="footer-uae-forum">Invest UAE Forum</button></li>
                <li><button onClick={() => navigate('/axis-circuit')} data-testid="footer-axis-circuit">The Axis Circuit™</button></li>
                <li><button onClick={() => navigate('/pitch-day')} data-testid="footer-pitch-day">Pitch Day</button></li>
                <li><button onClick={() => navigate('/jobs')} data-testid="footer-jobs">Job Board</button></li>
                <li><button onClick={() => navigate('/founders')} data-testid="footer-founders">Founders Directory</button></li>
              </ul>
            </div>

            <div className="f-col">
              <div className="f-col-title">Company</div>
              <ul>
                <li><button onClick={() => navigate('/about')} data-testid="footer-about">About</button></li>
                <li><button onClick={() => navigate('/pricing')} data-testid="footer-pricing">Pricing</button></li>
                <li><button onClick={() => navigate('/terms')} data-testid="footer-terms">Terms</button></li>
                <li><button onClick={() => navigate('/privacy')} data-testid="footer-privacy">Privacy</button></li>
              </ul>
            </div>
          </div>

          <div className="f-bottom">
            <div className="f-copy">© 2026 SKI Asia Pacific FZCO · All Rights Reserved</div>
            <div className="f-social">
              <a href="https://www.linkedin.com/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="footer-linkedin"><Linkedin size={16} /></a>
              <a href="mailto:hello@skiaxis.com" aria-label="Email" data-testid="footer-email"><Mail size={16} /></a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
